import axios from 'axios';
import get from 'lodash/get';
import axiosRetry from 'axios-retry';

import config from 'constants/config';
import triggerLoadingAction from 'helpers/loader';
import getAccessToken from 'helpers/getAccessToken';
import { logoutPlugin } from 'helpers/pluginAuthorization';
import { CANCEL_REQUESTS } from 'data/types/event.types';

// A 401-forced sign-out bypasses the signOut action (and its exitPlugin call),
// so the extension must be told here that the web session is gone. logoutPlugin
// swallows the not-installed case itself; the timestamp collapses a burst of
// parallel 401s into a single notification.
let lastForcedSignOutNotifyAt = 0;
const notifyPluginOfForcedSignOut = () => {
  const now = Date.now();
  if (now - lastForcedSignOutNotifyAt < 5000) return;
  lastForcedSignOutNotifyAt = now;
  logoutPlugin();
};

export default () =>
  ({ getState }) =>
  (next) =>
  async (action) => {
    if (!action) return false;
    const state = getState();
    const endpoint = get(action, 'payload.endpoint');
    const method = get(action, 'payload.method');
    const isCustom = get(action, 'payload.custom', false);
    const body = get(action, 'payload.body');
    const params = get(action, 'payload.params', {});
    const onSuccess = get(action, 'onSuccess', () => {});
    const onFailed = get(action, 'onFailed', () => {});
    const components = get(action, 'components', null);
    const skipAuth = get(action, 'payload.skipAuth', false);
    const delayLoad = get(action, 'payload.delayLoad', 1000);
    const responseType = get(action, 'payload.responseType');
    const showResponseError = get(action, 'payload.showResponseError', false);
    const ignore401 = get(action, 'payload.ignore401', false);
    const cancelToken = get(action, 'payload.cancelToken');
    const cancelEvent = get(action, 'payload.cancelEvent');
    const retries = get(action, 'payload.retries');
    // use it if you want to test some api endpoint which might be heavy
    const delayRequest = get(action, 'debug.delayRequest');
    const isDemoUser = get(state, 'user.isDemoUser');
    const token = get(action, 'accessToken');

    const apiHost = config.apiUrl;

    if (isCustom) {
      return next(action);
    }
    if (get(action, 'data')) return next(action);
    if (get(action, 'payload.event')) return next(action);

    let loading = true;
    setTimeout(() => {
      if (loading) {
        triggerLoadingAction(components, true, next);
      }
    }, delayLoad);

    try {
      const accessToken = skipAuth || token || (await getAccessToken());

      if (!accessToken) {
        loading = false;
        triggerLoadingAction(components, false, next);
        return next({
          type: 'SIGN_OUT',
          data: {},
        });
      }

      const client = axios.create({ baseURL: apiHost });
      if (retries) {
        axiosRetry(client, { retries, retryDelay: axiosRetry.exponentialDelay });
      }

      const res = await client({
        url: endpoint,
        method,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        data: body,
        params,
        responseType,
        cancelToken,
      });

      if (cancelEvent) window.removeEventListener(CANCEL_REQUESTS, cancelEvent);

      const data = get(res, 'data');
      if (onSuccess) onSuccess(data);

      loading = false;
      setTimeout(() => triggerLoadingAction(components, false, next), 0);

      if (process.env.NODE_ENV === 'development' && delayRequest) {
        setTimeout(() => {
          next({
            ...action,
            payload: data,
          });
        }, delayRequest);
        return false;
      }
      return next({
        ...action,
        payload: data,
      });
    } catch (err) {
      loading = false;
      setTimeout(() => triggerLoadingAction(components, false, next), 0);
      if (!axios.isCancel(err)) {
        if (get(err, 'response.status', '') === 401 && !ignore401) {
          // On first 401, try refreshing the token and retrying the request once
          if (!action._retried401) {
            // Clear the invalid token so getAccessToken() is forced to refresh
            // (jwt.decode doesn't verify signatures, so an invalid token may still appear unexpired)
            localStorage.removeItem('accessToken');
            const newAccessToken = await getAccessToken();
            if (newAccessToken) {
              try {
                const retryClient = axios.create({ baseURL: apiHost });
                const retryRes = await retryClient({
                  url: endpoint,
                  method,
                  headers: { Authorization: `Bearer ${newAccessToken}` },
                  data: body,
                  params,
                  responseType,
                  cancelToken,
                });
                const retryData = get(retryRes, 'data');
                if (onSuccess) onSuccess(retryData);
                return next({ ...action, _retried401: true, payload: retryData });
              } catch (retryErr) {
                if (get(retryErr, 'response.status', '') !== 401) {
                  // Non-401 error on retry - handle normally, don't sign out
                  if (onFailed) return onFailed(retryErr);
                  return false;
                }
              }
            }
          }
          localStorage.removeItem('accessToken');
          sessionStorage.removeItem('gpExtensionFreshLogin');
          notifyPluginOfForcedSignOut();
          return next({
            type: 'SIGN_OUT',
            data: {},
          });
        }
        if (isDemoUser && (err.response?.status === 429 || err.response?.status >= 500)) {
          next({
            type: 'MOVE_DEMO_TO_PROD',
            data: {
              code: err.response?.status,
            },
          });
        } else if (showResponseError) {
          if (typeof showResponseError !== 'function' || showResponseError(err)) {
            let header = get(err, 'response.data.message.header', get(err, 'response.data.header'));
            let text;

            if (err.response?.status === 400) {
              text = get(err, 'response.data.errors[0].details', '');
            }

            if (!text) {
              if (header) text = get(err, 'response.data.message.text', get(err, 'response.data.text'));
              else text = get(err, 'response.data.message', get(err, 'response.data'));
            }

            if ((!text && !header) || typeof text === 'object') {
              header = 'Something went wrong';
              text = '';
            }
            header = text || header;
            text = '';
            next({
              type: 'TOGGLE_MESSAGE',
              data: {
                type: 'error',
                options: {
                  header,
                  text,
                },
              },
            });
          }
        }
        if (onFailed) return onFailed(err);
      }
      return false;
    }
  };
